/**
 * Cheap chromatic aberration filter, used as a subtle shimmer when combo
 * crosses the 10x threshold. Offsets R and B channels along the sample
 * gradient of uTexture; strength decays over time via `setStrength`.
 */

import { Filter, GlProgram } from 'pixi.js';

const vertex = /* glsl */ `
  in vec2 aPosition;
  out vec2 vTextureCoord;
  uniform vec4 uInputSize;
  uniform vec4 uOutputFrame;
  uniform vec4 uOutputTexture;

  vec4 filterVertexPosition(void) {
      vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
      position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
      position.y = position.y * (2.0*uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
      return vec4(position, 0.0, 1.0);
  }

  vec2 filterTextureCoord(void) {
      return aPosition * (uOutputFrame.zw * uInputSize.zw);
  }

  void main(void) {
      gl_Position = filterVertexPosition();
      vTextureCoord = filterTextureCoord();
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  in vec2 vTextureCoord;
  out vec4 finalColor;
  uniform sampler2D uTexture;
  uniform float uStrength;

  void main() {
    vec2 uv = vTextureCoord;
    vec2 dir = uv - 0.5;
    float s = uStrength * 0.006;
    float r = texture(uTexture, uv - dir * s).r;
    vec4 ga = texture(uTexture, uv);
    float b = texture(uTexture, uv + dir * s).b;
    // preserve source alpha so transparent world-layer pixels stay
    // transparent (the background sprite lives below this filter)
    finalColor = vec4(r, ga.g, b, ga.a);
  }
`;

export class ChromaticAberrationFilter extends Filter {
  constructor() {
    const glProgram = GlProgram.from({ vertex, fragment, name: 'chroma' });
    super({
      glProgram,
      resources: {
        uniforms: { uStrength: { value: 0, type: 'f32' } },
      },
    });
  }

  setStrength(v: number): void {
    (this.resources.uniforms as any).uniforms.uStrength = v;
  }
}
